package ui

import kotlinx.atomicfu.locks.SynchronizedObject
import kotlinx.atomicfu.locks.synchronized
import org.koin.core.Koin
import org.koin.core.component.KoinComponent
import org.koin.core.qualifier.Qualifier
import org.koin.core.qualifier.named
import org.koin.core.scope.Scope
import org.koin.ext.getFullName
import kotlin.reflect.KClass

class SafeScopeHolder<VM : DisposableViewModel>(
    private val type: KClass<VM>,
    private val qualifier: Qualifier,
    private val koin: Koin,
) {
    private val lock = SynchronizedObject()
    private var attaches: Int = 0
    private var scope: Scope? = null

    fun attach(): VM = synchronized(lock) {
        val s = scope ?: koin.getOrCreateScope(type.getFullName(), qualifier).also { scope = it }
        attaches += 1
        s.get<VM>(type)
    }

    fun detach() = synchronized(lock) {
        if (attaches > 0) attaches -= 1
        if (attaches == 0) closeLocked()
    }

    fun forceClose() = synchronized(lock) {
        attaches = 0
        closeLocked()
    }

    private fun closeLocked() {
        val s = scope ?: return

        runCatching {
            s.get<VM>(type)
        }
            .getOrNull()
            ?.let { vm -> runCatching {
                vm.dispose()
            } }

        runCatching { s.close() }
        scope = null
    }
}

class ScopeHolderFactory: KoinComponent {
    private val lock = SynchronizedObject()
    private val holders = mutableMapOf<KClass<*>, SafeScopeHolder<*>>()

    @Suppress("UNCHECKED_CAST")
    fun <T : DisposableViewModel> getOrCreate(
        type: KClass<T>
    ): SafeScopeHolder<T> = synchronized(lock) {
        holders.getOrPut(type) {
            SafeScopeHolder(
                type = type,
                qualifier = named(type.getFullName()),
                koin = getKoin(),
            )
        } as SafeScopeHolder<T>
    }

    fun remove(type: KClass<*>) = synchronized(lock) { holders.remove(type) }
    fun clear() = synchronized(lock) { holders.clear() }
}